"use client";

import {
  Children,
  createContext,
  FC,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import cn from "classnames";

export type AutoScrollCarouselControlsContextValue = {
  goToPrevious: () => void;
  goToNext: () => void;
  pauseAutoplay: () => void;
  playAutoplay: () => void;
  isPlaying: boolean;
  canNavigate: boolean;
};

export const AutoScrollCarouselControlsContext =
  createContext<AutoScrollCarouselControlsContextValue>({
    goToPrevious: () => undefined,
    goToNext: () => undefined,
    pauseAutoplay: () => undefined,
    playAutoplay: () => undefined,
    isPlaying: false,
    canNavigate: false,
  });

const SPEED_PX_PER_SECOND = 32;
const STEP_DURATION_MS = 550;
const STEP_EASING = "cubic-bezier(.4,0,.2,1)";
const ANIMATION_BOOT_DELAY_MS = 300;

export type AutoScrollCarouselProps = {
  children?: ReactNode;
  className?: string;
  headerContent?: ReactNode;
  viewportClassName?: string;
} & HTMLAttributes<HTMLElement>;

export const AutoScrollCarousel: FC<AutoScrollCarouselProps> = ({
  children,
  className,
  headerContent,
  viewportClassName,
  ...containerAttributes
}) => {
  const slideItems = useMemo(() => Children.toArray(children), [children]);
  const slideCount = slideItems.length;
  const [isAnimationReady, setIsAnimationReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const isAnimationEnabled = slideCount > 1 && isAnimationReady;

  const trackRef = useRef<HTMLDivElement | null>(null);
  // Refs to all items in copy A (first set) — used to measure step size.
  const copyARefs = useRef<(HTMLDivElement | null)[]>([]);
  // Ref to the first item of copy B — used to measure half() (one full set width).
  const copyBFirstRef = useRef<HTMLDivElement | null>(null);
  const loopWidthRef = useRef(0);
  const stepSizeRef = useRef(0);

  // All engine state lives in refs — zero re-renders during animation,
  // exactly matching the designer's vanilla JS architecture.
  const offsetRef = useRef(0);
  const pausedRef = useRef(false);
  const manualMsRef = useRef(0); // ms remaining in a nudge window; RAF skips writing transform
  const isTransitioningRef = useRef(false);
  const clrTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transitionEndHandlerRef = useRef<
    ((event: TransitionEvent) => void) | null
  >(null);
  const nudgeRef = useRef<((dir: 1 | -1) => void) | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = (matches: boolean) => {
      pausedRef.current = matches;
      setIsPlaying(!matches);
      setPrefersReducedMotion(matches);
    };
    apply(mq.matches);
    const handler = (e: MediaQueryListEvent) => apply(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (slideCount <= 1) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let idleId: number | null = null;
    let loadHandler: (() => void) | null = null;

    const startWhenReady = () => {
      if (cancelled) return;
      timeoutId = setTimeout(() => {
        if (!cancelled) {
          setIsAnimationReady(true);
        }
      }, ANIMATION_BOOT_DELAY_MS);
    };

    const scheduleAfterLoad = () => {
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(startWhenReady, { timeout: 1200 });
        return;
      }

      startWhenReady();
    };

    if (document.readyState === "complete") {
      scheduleAfterLoad();
    } else {
      loadHandler = () => {
        scheduleAfterLoad();
      };
      window.addEventListener("load", loadHandler, { once: true });
    }

    return () => {
      cancelled = true;
      if (loadHandler) {
        window.removeEventListener("load", loadHandler);
      }
      if (idleId !== null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [slideCount]);

  useEffect(() => {
    if (!slideCount || !isAnimationEnabled) return;

    const track = trackRef.current;
    const card0 = copyARefs.current[0];

    if (!track || !card0 || !copyBFirstRef.current) return;

    const measure = () => {
      const firstCard = copyARefs.current[0];
      const firstCloneCard = copyBFirstRef.current;

      if (!firstCard || !firstCloneCard) return;

      loopWidthRef.current = firstCloneCard.offsetLeft - firstCard.offsetLeft;

      const secondCard = copyARefs.current[1];
      if (secondCard) {
        stepSizeRef.current = secondCard.offsetLeft - firstCard.offsetLeft;
        return;
      }

      const computed = window.getComputedStyle(track);
      stepSizeRef.current =
        firstCard.offsetWidth +
        (parseFloat(computed.columnGap || computed.gap || "0") || 0);
    };

    measure();

    offsetRef.current = 0;
    lastTsRef.current = null;
    track.style.transition = "none";
    track.style.transform = "translate3d(0px, 0, 0)";

    const removeTransitionEndHandler = () => {
      if (!transitionEndHandlerRef.current) return;

      track.removeEventListener(
        "transitionend",
        transitionEndHandlerRef.current,
      );
      transitionEndHandlerRef.current = null;
    };

    const resetTransitionState = () => {
      removeTransitionEndHandler();
      track.style.transition = "";
      manualMsRef.current = 0;
      isTransitioningRef.current = false;
    };

    // Nudge one card in a direction. dir=1 → left (next), dir=-1 → right (prev).
    function nudge(dir: 1 | -1) {
      if (!track) return;

      removeTransitionEndHandler();

      const shouldWrapBeforePrev =
        dir === -1 &&
        loopWidthRef.current > 0 &&
        offsetRef.current + stepSizeRef.current > 0;
      const shouldWrapBeforeNext =
        dir === 1 &&
        loopWidthRef.current > 0 &&
        offsetRef.current - stepSizeRef.current < -loopWidthRef.current;

      if (shouldWrapBeforePrev) {
        offsetRef.current -= loopWidthRef.current;
        track.style.transition = "none";
        track.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
        track.getBoundingClientRect();
      }

      if (shouldWrapBeforeNext) {
        offsetRef.current += loopWidthRef.current;
        track.style.transition = "none";
        track.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
        track.getBoundingClientRect();
      }

      track.style.transition = `transform ${STEP_DURATION_MS}ms ${STEP_EASING}`;
      isTransitioningRef.current = true;
      offsetRef.current -= dir * stepSizeRef.current;
      track.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
      // Block RAF from overwriting transform until the nudge really finishes.
      manualMsRef.current = STEP_DURATION_MS + 30;

      transitionEndHandlerRef.current = (event: TransitionEvent) => {
        if (event.propertyName !== "transform" || event.target !== track) {
          return;
        }

        resetTransitionState();
      };

      track.addEventListener("transitionend", transitionEndHandlerRef.current);

      if (clrTimerRef.current !== null) clearTimeout(clrTimerRef.current);
      clrTimerRef.current = setTimeout(() => {
        resetTransitionState();
        clrTimerRef.current = null;
      }, STEP_DURATION_MS + 80);
    }

    nudgeRef.current = nudge;

    function frame(now: number) {
      if (lastTsRef.current === null) lastTsRef.current = now;
      const dt = Math.min(now - lastTsRef.current, 60);
      lastTsRef.current = now;

      const prevOffset = offsetRef.current;

      // Advance offset only when not hovered and not in a nudge window.
      if (!pausedRef.current && manualMsRef.current <= 0) {
        offsetRef.current -= SPEED_PX_PER_SECOND * (dt / 1000); // px/s × s = px
      }
      if (manualMsRef.current > 0) {
        manualMsRef.current -= dt;
      }

      const h = loopWidthRef.current;
      if (!isTransitioningRef.current && h > 0) {
        while (offsetRef.current <= -h) offsetRef.current += h;
        while (offsetRef.current > 0) offsetRef.current -= h;
      }

      // Only write transform when not in a CSS-transition nudge window.
      if (
        manualMsRef.current <= 0 &&
        track &&
        offsetRef.current !== prevOffset
      ) {
        track.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
      }

      rafRef.current = requestAnimationFrame(frame);
    }

    rafRef.current = requestAnimationFrame(frame);

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);

    if (resizeObserver) {
      resizeObserver.observe(track);
    } else {
      window.addEventListener("resize", measure);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", measure);
      }
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (clrTimerRef.current !== null) {
        clearTimeout(clrTimerRef.current);
        clrTimerRef.current = null;
      }
      removeTransitionEndHandler();
      isTransitioningRef.current = false;
      nudgeRef.current = null;
    };
  }, [isAnimationEnabled, slideCount]);

  const handlePrev = () => {
    nudgeRef.current?.(-1);
  };

  const handleNext = () => {
    nudgeRef.current?.(1);
  };

  if (slideCount === 0) return null;

  return (
    <AutoScrollCarouselControlsContext.Provider
      value={{
        goToPrevious: handlePrev,
        goToNext: handleNext,
        isPlaying: isAnimationEnabled && isPlaying,
        canNavigate: isAnimationEnabled,
        pauseAutoplay: () => {
          setIsPlaying(false);
          pausedRef.current = true;
        },
        playAutoplay: () => {
          setIsPlaying(true);
          pausedRef.current = false;
        },
      }}
    >
      <section
        {...containerAttributes}
        className={cn("relative", className)}
        aria-roledescription="carousel"
      >
        {headerContent ? <div className="relative">{headerContent}</div> : null}

        <div className={cn("relative overflow-hidden", viewportClassName)}>
          <div
            ref={trackRef}
            className={cn(
              "flex items-stretch gap-5.5",
              isAnimationEnabled && "will-change-transform",
            )}
          >
            {/* Copy A — refs captured for measurement */}
            {slideItems.map((item, i) => (
              <div
                key={`a-${i}`}
                ref={(el) => {
                  copyARefs.current[i] = el;
                }}
                className="shrink-0 self-stretch"
              >
                {item}
              </div>
            ))}
            {/* Copy B — mounted only after initial paint to reduce LCP cost. */}
            {isAnimationEnabled &&
              slideItems.map((item, i) => (
                <div
                  key={`b-${i}`}
                  ref={
                    i === 0
                      ? (el) => {
                          copyBFirstRef.current = el;
                        }
                      : undefined
                  }
                  className="shrink-0 self-stretch"
                  aria-hidden="true"
                >
                  {item}
                </div>
              ))}
          </div>
        </div>
      </section>
    </AutoScrollCarouselControlsContext.Provider>
  );
};
